
<template>
  <div class="container mx-auto px-4 font-system sm:px-6 lg:px-8">
    <div v-if="loading" class="py-16 text-center text-text-muted">Loading...</div>

    <!-- Public site: always the marketing content, deterministically -- never
         conditioned on authState.isAuthenticated. The public site must stay
         session-blind even if a stale host-scoped cookie from before a domain cutover
         (theskyspeaks.com used to serve the application directly) is still present in
         the browser and would otherwise make authState.isAuthenticated spuriously
         true here. -->
    <template v-else-if="isPublicSite || !authState.isAuthenticated">
      <PageHeader
        title="What is The Sky Saying about You?"
        subtitle="Be Inspired by Listening To The Sky"
      />

      <div class="text-center">
        <p class="mt-4 text-base font-medium leading-5 sm:text-lg">
          Find how universal insight can be uniquely yours.
        </p>
        <div class="card mt-4 rounded bg-surface-2 p-4">
          <h5 class="mb-2 mt-5 font-medium leading-5">
            <i>
              "The stars are the cause of the world's movement;<br />
              The stars control the destiny of all living things."
            </i>
          </h5>
        </div>
        <span>&nbsp;<br /></span>
        <cite>Rumi's Paradox</cite>
        <span>&nbsp;<br /></span>
        <div class="card mt-4 rounded bg-surface-2 p-4">
          <h5 class="mb-2 mt-5 font-medium leading-5">
            <i>
              "The stars are not the cause of our fate;<br />
              They are only a sign, a symbol of what is to come.<br />
              To believe that they control our destiny<br />
              Is to live like a frog in a well, unable to see the larger world."
            </i>
          </h5>
        </div>
      </div>

      <div class="space-y-4 py-10 text-center">
        <RouterLink
          to="/signup"
          class="btn inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded bg-accent px-6 py-3 text-text-inverse hover:bg-accent-hover"
        >
          Create Your Chart
        </RouterLink>
        <p class="text-sm text-text-secondary">
          Already have an account?
          <RouterLink to="/login" class="text-accent-text underline">Log in</RouterLink>
        </p>
      </div>
    </template>

    <!-- Logged in, no chart: guided onboarding -->
    <template v-else-if="!hasChart">
      <PageHeader
        title="Create Your Birth Chart"
        subtitle="Three quick steps to calculate your natal chart."
      />

      <PageSection>
        <div class="w-full md:w-1/2">
          <BaseCard title="How it works">
            <ol class="list-decimal space-y-3 pl-5 text-sm text-text-secondary">
              <li>Enter your name</li>
              <li>Add your birth date and time</li>
              <li>Select your birth place from search results</li>
            </ol>
            <p class="mt-4 text-sm text-text-muted">
              We use your birth details to calculate and save your natal chart.
            </p>
          </BaseCard>

          <div class="mt-6 text-center">
            <RouterLink
              to="/create-chart"
              class="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg border border-accent bg-accent px-6 py-3 text-sm font-semibold text-text-inverse hover:bg-accent-hover"
            >
              Get Started
            </RouterLink>
          </div>
        </div>
      </PageSection>
    </template>

    <!-- Logged in, has chart: welcome + preview -->
    <template v-else>
      <PageHeader
        :title="welcomeTitle"
        subtitle="Here is a preview of your saved natal chart."
      />

      <PageSection>
        <div class="w-full md:w-1/2">
          <ChartSummaryCard
            compact
            :username="username"
            :chart-datetime="chartDatetime"
            :natal-angles="natalAngles"
            :natal-planets="natalPlanets"
            :load-error="loadError"
          />

          <div class="mt-6 flex flex-col items-center gap-3">
            <RouterLink
              to="/personal-sky"
              class="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg border border-accent bg-accent px-6 py-3 text-sm font-semibold text-text-inverse hover:bg-accent-hover"
            >
              View Personal Sky
            </RouterLink>
            <RouterLink to="/create-chart" class="text-sm text-accent-text underline">
              Update birth data
            </RouterLink>
          </div>
        </div>
      </PageSection>
    </template>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { RouterLink } from 'vue-router';
import PageHeader from '@/components/common/PageHeader.vue';
import PageSection from '@/components/common/PageSection.vue';
import BaseCard from '@/components/common/BaseCard.vue';
import ChartSummaryCard from '@/components/chart/ChartSummaryCard.vue';
import { authState } from '@/utils/auth';
import { useChartStatus } from '@/composables/useChartStatus';
import { isPublicSite } from '@/utils/siteContext';

const { loading, hasChart, chartData, loadError } = useChartStatus();

const natalPlanets = computed(() => chartData.value?.planets || []);
const natalAngles = computed(() => chartData.value?.angles || []);
const chartDatetime = computed(() => chartData.value?.datetime || null);
const username = computed(() =>
  [chartData.value?.firstName, chartData.value?.lastName].filter(Boolean).join(' ')
);

const welcomeTitle = computed(() => {
  const firstName = chartData.value?.firstName;
  return firstName ? `Welcome back, ${firstName}` : 'Welcome back';
});
</script>
